import type { ReactNode } from 'react';

import { Skeleton } from '@/_shared/components/ui/skeleton';

import { AttributePredicateClause } from './AttributePredicateClause';
import { ConnectorPill } from './ConnectorPill';
import type { ChildConnector } from './GroupCard';
import { ClauseGroupCard, GroupCard } from './GroupCard';
import { assertNever, isGroup, pruneEmptyGroups } from './lib/tree';
import { RawSqlClause } from './RawSqlClause';
import type { AudienceDefinition, AudienceView, Predicate, Row } from './types';

/** The builder's rendering state. `empty` is derived (no `where`); `loading` / `disabled` are explicit. */
export type PredicateTreeState = 'ready' | 'loading' | 'disabled';

/** Context threaded through every node — the id→Row lookup, the disabled flag, and read-only mode. */
interface TreeContext {
  rowsById: Map<string, Row>;
  disabled: boolean;
  readOnly: boolean;
  defaultInsightOpen: boolean;
  onRawSqlViewed?: () => void;
}

/** The single entry point. Renders (and edits) `view.definition.expression.where`, joining each leaf to its
 *  Row (from `view.rows`) by `id` for the column `type` + grounded control. `where` absent ⇒ empty state;
 *  `state` gates the loading skeleton and the disabled (greyed, read-only) rendering. An edit returns a NEW
 *  {@link AudienceDefinition} (rows are derived grounding, never edited by the tree). */
export function PredicateTree({
  view,
  onChange,
  state = 'ready',
  defaultInsightOpen = false,
  onRawSqlViewed,
}: {
  view: AudienceView;
  onChange?: (next: AudienceDefinition) => void;
  state?: PredicateTreeState;
  /** Start each grounded clause with its ✨ panel open (audience definition page). */
  defaultInsightOpen?: boolean;
  /** A rawSql clause opened its SQL. The tree does not know the audience, so the caller reports it. */
  onRawSqlViewed?: () => void;
}) {
  if (state === 'loading') return <TreeSkeleton />;

  const { definition, rows } = view;
  const where = definition.expression.where;
  const normalized = where ? pruneEmptyGroups(where) : null;
  if (!normalized) return <EmptyState />;

  // Read-only when no `onChange` is given — the display surface (audience details page) omits it, so no
  // delete/edit affordances render. `rows` may be empty (backend hasn't attached them) ⇒ every leaf read-only.
  const readOnly = !onChange;
  const rowsById = new Map(rows.map((row) => [row.id, row]));
  const ctx: TreeContext = { rowsById, disabled: state === 'disabled', readOnly, defaultInsightOpen, onRawSqlViewed };

  // Prune empty AND/OR blocks after every edit: deleting a block's last entry removes the block (and, if
  // that empties the whole tree, `where` becomes absent → the empty state).
  const setWhere = (next: Predicate) => {
    const pruned = pruneEmptyGroups(next);
    onChange?.({ ...definition, expression: { ...definition.expression, where: pruned ?? undefined } });
  };

  // The tree stays within its container — each clause scrolls its OWN content horizontally (see
  // ReadOnlyClause / AttributePredicateClause), so a wide sentence never widens the whole tree and the
  // row actions stay pinned to the right, always on screen.
  // A group root cards its own children (GroupCard). A single bare-leaf root has no group, so card it here
  // too — with a neutral (grey) rail, since one condition has no AND/OR combinator to colour.
  const root = <PredicateNode predicate={normalized} onChange={setWhere} ctx={ctx} isRoot />;
  return <div className="min-w-0">{isGroup(normalized) ? root : <ClauseGroupCard>{root}</ClauseGroupCard>}</div>;
}

/** The recursive dispatcher: switches exhaustively on which single key the node carries and ends in
 *  `assertNever`. Threads a child-scoped `onChange` (rebuilds the subtree into its parent) and an
 *  `onRemove` supplied by the parent group; the root gets neither. */
function PredicateNode({
  predicate,
  onChange,
  onRemove,
  ctx,
  isRoot = false,
  connector = null,
}: {
  predicate: Predicate;
  onChange: (next: Predicate) => void;
  onRemove?: () => void;
  ctx: TreeContext;
  isRoot?: boolean;
  connector?: ChildConnector;
}): ReactNode {
  const { rowsById, disabled, readOnly, defaultInsightOpen, onRawSqlViewed } = ctx;
  const hideLabel = !!connector;
  // Read-only tree offers no delete action.
  const remove = readOnly ? undefined : onRemove;

  // The AND/OR connector is a SIBLING relationship, so the TREE owns it: wrap the node with the
  // connector inline-left, aligned with the clause; the node's own grounding stays indented under it.
  const withConnector = (node: ReactNode): ReactNode =>
    connector ? (
      <div className="flex min-w-0 items-start gap-2">
        <span className="flex h-8 shrink-0 items-center">
          <ConnectorPill op={connector.op} />
        </span>
        <div className="min-w-0 flex-1">{node}</div>
      </div>
    ) : (
      node
    );

  if ('attribute' in predicate) {
    const leaf = predicate.attribute;
    const row = leaf.id != null ? rowsById.get(leaf.id) : undefined; // no Row ⇒ non-editable
    return withConnector(
      <AttributePredicateClause
        predicate={leaf}
        type={row?.type}
        control={row?.control}
        sentence={row?.sentence}
        label={row?.label}
        bounds={row?.bounds}
        nonNullFraction={row?.nonNullFraction}
        onChange={(next) => onChange({ attribute: next })}
        onRemove={remove}
        disabled={disabled}
        readOnly={readOnly}
        hideLabel={hideLabel}
        defaultInsightOpen={defaultInsightOpen}
      />,
    );
  }
  if ('and' in predicate || 'or' in predicate) {
    return withConnector(
      <GroupCard
        group={predicate}
        onChange={onChange}
        isRoot={isRoot}
        renderChild={(child, onChildChange, onChildRemove, childConnector) => (
          <PredicateNode
            predicate={child}
            onChange={onChildChange}
            onRemove={onChildRemove}
            ctx={ctx}
            connector={childConnector}
          />
        )}
      />,
    );
  }
  if ('rawSql' in predicate) {
    return withConnector(
      <RawSqlClause predicate={predicate.rawSql} onRemove={remove} disabled={disabled} onShowSql={onRawSqlViewed} />,
    );
  }
  return assertNever(predicate);
}

/** No `where` — the audience matches everyone. Rendered in the same neutral (grey-railed) card as a
 *  condition, so the definition surface stays visually consistent. */
function EmptyState() {
  return (
    <ClauseGroupCard>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-grey-800">Everyone</p>
        <p className="text-sm text-muted-foreground">No conditions yet — everyone qualifies</p>
      </div>
    </ClauseGroupCard>
  );
}

/** Loading — the definition is still resolving. */
function TreeSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading audience">
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-8 w-64" />
    </div>
  );
}
